"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";
import type { Product } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Bed,
  Bath,
  Users,
  MapPin,
  CalendarDays,
  CheckCircle2,
  Tag,
  ArrowLeft,
  Lock,
  Users2,
} from "lucide-react";
import {
  formatDate,
  formatDateShort,
  formatPrice,
  nightsBetween,
  amenityList,
  propertyTypeLabel,
  bookingTypeLabel,
  bookingTypeBadgeClass,
} from "@/components/booking/format";
import { toast } from "sonner";
import { useMemo, useState } from "react";import { useI18n } from "@/lib/i18n/provider";

export function ProductDetailView() {
  const id = useAppStore((s) => s.selectedProductId);
  const goBrowse = useAppStore((s) => s.goBrowse);
  const user = useAppStore((s) => s.user);
  const openAuth = useAppStore((s) => s.openAuth);
  const qc = useQueryClient();
  const { t, locale } = useI18n();

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [partySize, setPartySize] = useState(1);
  const [busy, setBusy] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: () => apiGet<{ product: Product }>(`/api/products/${id}`),
    enabled: !!id,
  });

  const product = data?.product;
  const images = product?.images ?? [];
  const nights =
    checkIn && checkOut
      ? nightsBetween(new Date(checkIn).toISOString(), new Date(checkOut).toISOString())
      : 0;

  const bookedRanges =
    product?.reservations?.filter((r) => r.status === "APPROVED") ?? [];

  // The stay's booking type is a per-request choice (private exclusive vs
  // prorata shared) — the property itself allows both.
  const [bookingType, setBookingType] = useState<"PRIVATE" | "SHARED">("PRIVATE");
  const isShared = bookingType === "SHARED";

  // Per-type availability on the chosen dates:
  //   PRIVATE request  → blocked by any approved overlap (exclusive).
  //   SHARED request   → blocked by an approved PRIVATE overlap; shares capacity
  //                      with other approved SHARED stays.
  const overlapsChosen = (list: typeof bookedRanges) => {
    if (!checkIn || !checkOut) return false;
    const s = new Date(checkIn).getTime();
    const e = new Date(checkOut).getTime();
    return list.some(
      (r) => new Date(r.endDate).getTime() > s && new Date(r.startDate).getTime() < e
    );
  };
  const approvedPrivateOverlap = overlapsChosen(
    bookedRanges.filter((r) => r.bookingType === "PRIVATE")
  );
  const approvedAnyOverlap = overlapsChosen(bookedRanges);

  // ---- Shared booking: projected price + co-occupants ----
  const approvedShared = bookedRanges.filter((r) => r.bookingType === "SHARED");
  const coOccupants = useMemo(() => {
    if (!isShared || !checkIn || !checkOut) return [];
    const start = new Date(checkIn).getTime();
    const end = new Date(checkOut).getTime();
    return approvedShared.filter(
      (r) => new Date(r.endDate).getTime() > start && new Date(r.startDate).getTime() < end
    );
  }, [isShared, checkIn, checkOut, approvedShared]);

  // Projected total for shared = sum over days of (partySize / (coOccupantsThatDay + partySize)) * rate
  const projectedSharedTotal = useMemo(() => {
    if (!isShared || !product || nights <= 0) return 0;
    const rate = product.pricePerNight;
    let total = 0;
    const start = new Date(checkIn);
    start.setUTCHours(0, 0, 0, 0);
    for (let i = 0; i < nights; i++) {
      const day = new Date(start);
      day.setUTCDate(day.getUTCDate() + i);
      const dayMs = day.getTime();
      const occ =
        coOccupants
          .filter((r) => new Date(r.startDate).getTime() <= dayMs && dayMs < new Date(r.endDate).getTime())
          .reduce((s, r) => s + r.partySize, 0) + Math.max(1, partySize);
      total += (Math.max(1, partySize) / occ) * rate;
    }
    return Math.round(total * 100) / 100;
  }, [isShared, product, nights, checkIn, coOccupants, partySize]);

  const totalPrice = isShared ? projectedSharedTotal : product ? nights * product.pricePerNight : 0;

  const reserve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      openAuth("login");
      return;
    }
    if (!checkIn || !checkOut) {
      toast.error(t("toast.pleaseChooseDates"));
      return;
    }
    setBusy(true);
    try {
      await apiPost("/api/reservations", {
        productId: id,
        bookingType,
        startDate: checkIn,
        endDate: checkOut,
        partySize: Math.max(1, partySize),
      });
      toast.success(t("toast.reservationRequested"));
      setCheckIn("");
      setCheckOut("");
      setPartySize(1);
      qc.invalidateQueries({ queryKey: ["product", id] });
      qc.invalidateQueries({ queryKey: ["reservations"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotCreate"));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <Skeleton className="mb-6 h-6 w-32" />
        <div className="grid gap-8 lg:grid-cols-3">
          <Skeleton className="aspect-[16/10] lg:col-span-2 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6">
        <p className="text-lg font-medium">{t("common.loading")}</p>
        <Button onClick={goBrowse} className="mt-4">
          {t("detail.backToAll")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <Button variant="ghost" size="sm" onClick={goBrowse} className="mb-4 gap-2">
        <ArrowLeft className="h-4 w-4" /> {t("detail.backToAll")}
      </Button>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Left: gallery + info */}
        <div className="lg:col-span-2">
          <div className="overflow-hidden rounded-2xl bg-muted">
            <div className="relative aspect-[16/10] w-full">
              {images[activeImage] ? (
                 
                <img
                  src={images[activeImage].url}
                  alt={images[activeImage].caption || product.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="grid h-full w-full place-items-center text-muted-foreground">
                  {t("common.noImage")}
                </div>
              )}
              <Badge className="absolute left-4 top-4 bg-background/90 text-foreground">
                {propertyTypeLabel(product.propertyType, locale)}
              </Badge>
            </div>
          </div>

          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {images.map((im, i) => (
                <button
                  key={im.id}
                  onClick={() => setActiveImage(i)}
                  className={`relative aspect-[4/3] w-28 shrink-0 overflow-hidden rounded-lg border-2 transition ${
                    i === activeImage ? "border-primary" : "border-transparent opacity-80 hover:opacity-100"
                  }`}
                >
                  { }
                  <img src={im.url} alt={im.caption || ""} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {product.title}
              </h1>
              <div className="mt-1 flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {product.location}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 border-y py-4">
              <Stat icon={Bed} label={`${product.bedrooms}`} />
              <Stat icon={Bath} label={`${product.bathrooms}`} />
              <Stat icon={Users} label={`${product.maxGuests} ${t("guests")}`} />
              <Stat icon={Tag} label={`${formatPrice(product.pricePerNight)} ${t("card.perNight")}`} />
            </div>

            <div>
              <h2 className="mb-2 text-lg font-semibold">{t("detail.about")}</h2>
              <p className="whitespace-pre-line leading-relaxed text-muted-foreground">
                {product.description}
              </p>
            </div>

            {amenityList(product.amenities).length > 0 && (
              <div>
                <h2 className="mb-2 text-lg font-semibold">{t("detail.amenities")}</h2>
                <div className="flex flex-wrap gap-2">
                  {amenityList(product.amenities).map((a) => (
                    <Badge key={a} variant="secondary" className="gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {a}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="mb-2 text-lg font-semibold">{t("detail.availability")}</h2>
              {product.availabilities && product.availabilities.length > 0 ? (
                <ul className="grid gap-2 sm:grid-cols-2">
                  {product.availabilities.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm"
                    >
                      <CalendarDays className="h-4 w-4 text-emerald-600" />
                      {formatDate(a.startDate, locale)} — {formatDate(a.endDate, locale)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">{t("detail.noAvailability")}</p>
              )}
            </div>

            {bookedRanges.length > 0 && (
              <div>
                <h2 className="mb-2 text-lg font-semibold">{t("detail.alreadyBooked")}</h2>
                <ul className="flex flex-wrap gap-2">
                  {bookedRanges.map((r) => (
                    <li key={r.id}>
                      {r.bookingType === "SHARED" ? (
                        <Badge
                          variant="outline"
                          className="gap-1 border-violet-200 bg-violet-50 text-violet-800"
                        >
                          <Users2 className="h-3 w-3" /> {formatDateShort(r.startDate, locale)} —{" "}
                          {formatDateShort(r.endDate, locale)} · {r.partySize} {t("guests")}
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="gap-1 border-rose-200 bg-rose-50 text-rose-700"
                        >
                          <Lock className="h-3 w-3" /> {formatDateShort(r.startDate, locale)} —{" "}
                          {formatDateShort(r.endDate, locale)}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">{t("detail.bookedLegend")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: reservation panel */}
        <div>
          <Card className="sticky top-20">
            <CardHeader>
              <CardTitle className="flex items-baseline justify-between">
                <span>{formatPrice(product.pricePerNight)}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {t("detail.perNight")}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!user ? (
                <div className="grid gap-3">
                  <Button size="lg" className="w-full" onClick={() => openAuth("login")}>
                    {t("detail.signInToReserve")}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    {t("detail.requestHelpText")}
                  </p>
                </div>
              ) : (
              <form onSubmit={reserve} className="grid gap-4">
                {/* Booking type chooser: per-stay decision */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBookingType("PRIVATE")}
                    className={`rounded-lg border p-3 text-start text-sm transition ${
                      !isShared
                        ? "border-rose-400 bg-rose-50 text-rose-900 ring-1 ring-rose-300"
                        : "border-border bg-card text-muted-foreground hover:border-sky-200"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-medium">
                      <Lock className="h-4 w-4" /> {t("booking.privateTitle")}
                    </div>
                    <div className="mt-0.5 text-[11px] leading-tight">
                      {t("booking.PRIVATE.short")}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookingType("SHARED")}
                    className={`rounded-lg border p-3 text-start text-sm transition ${
                      isShared
                        ? "border-sky-400 bg-sky-50 text-sky-900 ring-1 ring-sky-300"
                        : "border-border bg-card text-muted-foreground hover:border-sky-200"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-medium">
                      <Users2 className="h-4 w-4" /> {t("booking.sharedTitle")}
                    </div>
                    <div className="mt-0.5 text-[11px] leading-tight">
                      {t("booking.SHARED.short")}
                    </div>
                  </button>
                </div>
                <p className="-mt-1 text-xs leading-relaxed text-muted-foreground">
                  {isShared ? t("booking.SHARED.desc") : t("booking.PRIVATE.desc")}
                </p>
                {approvedPrivateOverlap && nights > 0 && (
                  <p className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                    {t("detail.privateBlockHint")}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="checkIn">{t("detail.checkIn")}</Label>
                    <Input
                      id="checkIn"
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      required
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="checkOut">{t("detail.checkOut")}</Label>
                    <Input
                      id="checkOut"
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Party size (always shown; meaningful for both, drives shared split) */}
                <div className="grid gap-1.5">
                  <Label htmlFor="partySize">
                    {isShared ? t("booking.field.partySize") : t("booking.field.partySize")}
                  </Label>
                  <Input
                    id="partySize"
                    type="number"
                    min={1}
                    max={product?.maxGuests ?? 100}
                    value={partySize}
                    onChange={(e) =>
                      setPartySize(Math.max(1, Math.min(Number(e.target.value) || 1, product?.maxGuests ?? 100)))
                    }
                    required
                  />
                  <p className="text-xs text-muted-foreground">{t("booking.field.partySizeHint")}</p>
                </div>

                {nights > 0 && (
                  <div className="space-y-1 rounded-lg border bg-muted/40 p-3 text-sm">
                    {isShared ? (
                      <>
                        <Row
                          label={t("booking.yourShare")}
                          value={formatPrice(totalPrice)}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t("booking.yourShareHint")}
                        </p>
                      </>
                    ) : (
                      <Row
                        label={t("detail.priceRow", {
                          price: formatPrice(product.pricePerNight),
                          nights,
                          plural: nights !== 1 ? "s" : "",
                        })}
                        value={formatPrice(totalPrice)}
                      />
                    )}
                    <div className="flex justify-between border-t pt-1 font-semibold">
                      <span>{t("common.total")}</span>
                      <span>{formatPrice(totalPrice)}</span>
                    </div>
                  </div>
                )}

                {isShared && coOccupants.length > 0 && nights > 0 && (
                  <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 text-sm">
                    <div className="mb-1 flex items-center gap-2 font-medium text-violet-900">
                      <Users2 className="h-4 w-4" />
                      {t("booking.coOccupants")}
                    </div>
                    <ul className="space-y-1 text-muted-foreground">
                      {coOccupants.map((c) => (
                        <li key={c.id} className="flex items-center justify-between gap-2">
                          <span>
                            {formatDateShort(c.startDate, locale)} — {formatDateShort(c.endDate, locale)}
                          </span>
                          <Badge variant="outline" className="border-violet-200 text-violet-800">
                            {c.partySize} {c.partySize === 1 ? t("guests") : t("guests")}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {isShared && coOccupants.length === 0 && nights > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t("booking.noCoOccupants")}
                  </p>
                )}

                <Button type="submit" disabled={busy} size="lg" className="w-full">
                  {busy ? t("detail.requesting") : t("detail.requestReservation")}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  {t("detail.requestHelpText")}
                </p>
              </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label }: { icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground" />
      {label}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
